/**
 * Checks if a value is a plain object (not an array, null, File, Date, etc.)
 * @param {*} v - The value to check
 * @returns {boolean} True if the value is a plain object
 */
const isPlainObject = (v) => {
    if (!v || typeof v !== 'object') return false

    const proto = Object.getPrototypeOf(v)
    return proto === Object.prototype || proto === null
}

const isFileLike = (v) => {
    if (typeof File !== 'undefined' && v instanceof File) return true
    return typeof Blob !== 'undefined' && v instanceof Blob
}

const hasFileValue = (v) => {
    if (isFileLike(v)) return true
    if (Array.isArray(v)) return v.some(hasFileValue)
    if (isPlainObject(v)) {
        return Object.keys(v).some((key) => hasFileValue(v[key]))
    }
    return false
}

const appendFormData = (formData, key, value) => {
    if (value === undefined) return

    if (value === null) {
        formData.append(key, '')
        return
    }

    if (isFileLike(value)) {
        formData.append(key, value)
        return
    }

    if (Array.isArray(value)) {
        value.forEach((item) => {
            appendFormData(formData, `${key}[]`, item)
        })
        return
    }

    if (isPlainObject(value)) {
        Object.keys(value).forEach((childKey) => {
            appendFormData(formData, `${key}[${childKey}]`, value[childKey])
        })
        return
    }

    formData.append(key, value)
}

const toFormData = (data = {}) => {
    const formData = new FormData()

    Object.keys(data).forEach((key) => {
        appendFormData(formData, key, data[key])
    })

    return formData
}

/**
 * Joins two path strings with a dot
 * @param {string} a - The first part of the path
 * @param {string} b - The second part of the path
 * @returns {string} The joined path
 */
const joinPath = (a, b) => {
    if (!a) return b || ''
    if (!b) return a
    return `${a}.${b}`
}

/**
 * Retrieves a value from an object using a dot-notated path
 * @param {Object} obj - The object to traverse
 * @param {string} path - The dot-notated path
 * @returns {*} The value at the path, or undefined
 */
const getByPath = (obj, path) => {
    if (!obj || !path) return undefined

    //Exact key match first (supports flat configs keyed by full mapping)
    if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path]

    //Then treat as dot-path for nested configs
    const parts = path.split('.')
    let cur = obj

    for (let i = 0; i < parts.length; i += 1) {
        if (!cur || !Object.prototype.hasOwnProperty.call(cur, parts[i]))
            return undefined
        cur = cur[parts[i]]
    }

    return cur
}

/**
 * Sets a value in an object using a dot-notated path
 * @param {Object} obj - The object to modify
 * @param {string} path - The dot-notated path
 * @param {*} value - The value to set
 */
const setByPath = (obj, path, value) => {
    const parts = path.split('.')
    let cur = obj

    for (let i = 0; i < parts.length - 1; i += 1) {
        const k = parts[i]
        if (!isPlainObject(cur[k])) cur[k] = {}
        cur = cur[k]
    }

    cur[parts[parts.length - 1]] = value
}

/**
 * Checks if an object has a property at the given path
 * @param {Object} obj - The object to check
 * @param {string} path - The dot-notated path
 * @returns {boolean} True if the path exists in the object
 */
const hasByPath = (obj, path) => {
    if (!obj || !path) return false

    // Exact key match first supports flat configs keyed by full mapping
    if (Object.prototype.hasOwnProperty.call(obj, path)) return true

    // Then treat as dot-path for nested configs
    const parts = path.split('.')
    let cur = obj

    for (let i = 0; i < parts.length; i += 1) {
        if (!cur || !Object.prototype.hasOwnProperty.call(cur, parts[i]))
            return false
        cur = cur[parts[i]]
    }

    return true
}

/**
 * Deep clones a value (handles arrays and plain objects)
 * @param {*} v - The value to clone
 * @returns {*} The cloned value
 */
const deepClone = (v) => {
    if (Array.isArray(v)) return v.map(deepClone)
    if (isPlainObject(v)) {
        const o = {}
        Object.keys(v).forEach((k) => {
            o[k] = deepClone(v[k])
        })
        return o
    }
    return v
}

/**
 * Deeply merges source object into target object (mutates target)
 * @param {Object} target - The target object to merge into
 * @param {Object} source - The source object to merge from
 * @returns {Object} The mutated target object
 */
const deepMerge = (target, source) => {
    //Mutates target
    Object.keys(source).forEach((k) => {
        const sv = source[k]
        const tv = target[k]

        if (isPlainObject(tv) && isPlainObject(sv)) {
            deepMerge(tv, sv)
            return
        }

        target[k] = deepClone(sv)
    })

    return target
}

/**
 * Hydrates a configuration object from a schema and base configuration
 * @param {Object} schema - The schema containing fields and values
 * @param {Object} baseConfig - The base configuration object
 * @param {Object} options - Hydration options
 * @param {string} [options.mode='merge'] - 'merge' preserves baseConfig keys not in schema; 'replace' returns only schema-mapped keys
 * @param {boolean} [options.includeUndefined=false] - Whether to include undefined values
 * @param {boolean} [options.includeEmptyStrings=true] - Whether to include empty strings
 * @param {boolean} [options.includeNull=true] - Whether to include null values
 * @param {boolean} [options.includeContainerMappings] - Whether section/group mappings should prefix child field paths
 * @param {boolean} [options.ignoreInSubmission=true] - Whether sections marked ignore_in_submission should be skipped
 * @returns {Object} The hydrated configuration
 */
const hydrateConfig = (schema = {}, baseConfig = {}, options = {}) => {
    const {
        mode = 'merge', //merge preserves baseConfig keys not in schema; replace returns only schema-mapped keys
        includeUndefined = mode === 'replace',
        includeEmptyStrings = true,
        includeNull = true,
        includeContainerMappings = mode !== 'replace',
        ignoreInSubmission = true,
    } = options

    const shouldIncludeValue = (v) => {
        if (v === undefined) return includeUndefined
        if (v === null) return includeNull
        if (v === '') return includeEmptyStrings
        return true //includes false, 0, [] etc
    }

    const patch = {}

    const walk = (node, parentPath = '', isRoot = false) => {
        if (!node) return

        if (node.type === 'alert' || node.type === 'markdown') return

        const isContainer = Array.isArray(node.fields)

        if (
            ignoreInSubmission &&
            !isRoot &&
            isContainer &&
            node.ignore_in_submission === true
        ) {
            return
        }

        const nodePath =
            isContainer && !includeContainerMappings
                ? parentPath
                : node.mapping
                  ? joinPath(parentPath, node.mapping)
                  : parentPath

        if (isContainer) {
            node.fields.forEach((child) => walk(child, nodePath, false))
            return
        }

        if (!node.mapping) return

        const fullPath = joinPath(parentPath, node.mapping)
        const v = node.value

        if (!shouldIncludeValue(v)) return

        setByPath(patch, fullPath, v)
    }

    walk(schema, '', true)

    //2) Merge or replace
    if (mode === 'replace') return patch

    const out = deepClone(baseConfig || {})
    return deepMerge(out, patch)
}

/**
 * Hydrates a schema object with values from a configuration object
 * @param {Object} schema - The schema to hydrate
 * @param {Object} config - The configuration object containing values
 * @returns {Object} The hydrated schema
 */
const hydrateSchema = (schema = {}, config = {}) => {
    const walk = (node, parentPath = '') => {
        if (!node) return node

        //If this node is a section/group, it contributes to the path for its children
        const nodePath = node.mapping
            ? joinPath(parentPath, node.mapping)
            : parentPath

        //If it has children, recurse
        if (Array.isArray(node.fields)) {
            return {
                ...node,
                fields: node.fields.map((child) => walk(child, nodePath)),
            }
        }

        //Leaf field: mapping is relative to current parent path
        if (!node.mapping) return node

        const fullPath = joinPath(parentPath, node.mapping)

        // Only override when the config actually contains this path.
        // This still allows false, 0, "", null, and even explicit undefined.
        if (!hasByPath(config, fullPath)) return node

        let finalValue = getByPath(config, fullPath)

        // Make sure boolean values are actually booleans and not strings
        if (
            node.type === 'boolean' &&
            finalValue !== undefined &&
            finalValue !== null
        ) {
            if (typeof finalValue === 'string') {
                finalValue = finalValue.toLowerCase() === 'true'
            } else {
                finalValue = Boolean(finalValue)
            }
        }

        return {
            ...node,
            value: finalValue,
        }
    }

    return walk(schema, '')
}

export { hydrateSchema, hydrateConfig, deepMerge, toFormData, hasFileValue }
